import { useQuery } from '@tanstack/react-query';
import { fetchGql } from '@/lib/graphql_client';
import { MyRelationshipsDocument } from '@/gql/graphql';
import { useContextStore } from '@/lib/session/context-store';
import { mapRelationship } from './map-relationship';
import type { RelationshipRowVM } from './relationships.types';

/**
 * Keyed by active tenant AND `actingTenantId` so neither a context switch nor an
 * oversight drill-down (ENT-89 T08) ever serves another tenant's cached list.
 */
export const myRelationshipsKey = (tenantId?: string, actingTenantId?: string) =>
  ['relationships', 'mine', tenantId ?? 'self', actingTenantId ?? 'self'] as const;

/**
 * Cross-org relationships (ENT-89 §3), mapped to direction-aware view models.
 *
 * Default: the active context's own relationships. Pass `actingTenantId` to
 * project another tenant's relationships (platform-admin oversight, T08) — this
 * is grant-gated server-side; a missing grant rejects with
 * `ACL_OVERSIGHT_FORBIDDEN` (surfaced via `isError`). Cache is also invalidated
 * wholesale by `useSwitchContext` on context switch. `enabled: false` fires no read
 * (a caller that needs the list only for some personas).
 */
export function useMyRelationships(actingTenantId?: string, { enabled = true }: { enabled?: boolean } = {}) {
  const tenantId = useContextStore((s) => s.active?.tenantId);
  return useQuery<RelationshipRowVM[]>({
    queryKey: myRelationshipsKey(tenantId, actingTenantId),
    enabled,
    queryFn: async () => {
      const data = await fetchGql(MyRelationshipsDocument, { actingTenantId });
      return data.myRelationships.map(mapRelationship);
    },
    // An oversight denial is expected, not transient — don't retry it.
    retry: actingTenantId ? false : 1,
  });
}
